/**
 * Camera connectivity check with its controls (ENG-2457): the connector's own
 * report, the "Test cameras" button, a running test, and the latest test's
 * result. Used on the site page (dialog + onboarding card), the camera page
 * (one camera) and the Troubleshooter.
 *
 * Every customer sees results (not gated on Camera Health Monitoring). Only
 * users who can edit the site get the button; the server enforces the same.
 */
import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { TsButton } from "../../Troubleshooting/TroubleshootingUi";
import { SiteConnectivityPanel } from "./SiteConnectivityPanel";
import {
  dryRunSupported,
  familyOf,
  formatClock,
  runProgress,
} from "./connectivityModel";
import { StreamTestButton } from "../StreamTest/StreamTestButton";
import { useCameraDryRun, DryRunNotice } from "./useCameraDryRun";
import { useSiteConnectivity } from "./useSiteConnectivity";
import {
  CameraConnectivity,
  ConnectivityResult,
} from "./siteConnectivityTypes";

export const TEST_UNAVAILABLE_TEXT = "Full camera test isn't available yet";
export const TEST_NOT_SUPPORTED_TEXT =
  "Full camera test isn't available for this site";
export const CLUSTER_BUSY_TEXT =
  "Our test capacity is busy right now — try again in a few minutes.";
export const TEST_BUSY_TEXT =
  "A test is already running for this site. Its results will show here when it finishes.";

export const rateLimitText = (retryAt: string | null): string =>
  `You can run a test once an hour for this site. ${
    retryAt
      ? `You can run the next one at ${formatClock(retryAt)}.`
      : "Try again later."
  }`;

export const noticeText = (notice: DryRunNotice): string => {
  switch (notice.kind) {
    case "busy":
      return TEST_BUSY_TEXT;
    case "rate_limited":
      return rateLimitText(notice.retryAt);
    case "cluster_busy":
      return CLUSTER_BUSY_TEXT;
    default:
      return notice.message;
  }
};

type Shown = "test" | "connector";

const Spinner = ({ text }: { text: string }) => (
  <div
    className="flex items-center gap-3 py-4 font-mulish text-[14px] text-[#4c5257]"
    role="status"
  >
    <CircularProgress size={18} sx={{ color: "#FF8400" }} />
    {text}
  </div>
);

export const runningText = (
  result: ConnectivityResult | null,
  single: boolean
): string => {
  // Count cameras that have answered, not cameras dispatched: admin lists
  // every camera as soon as the run starts.
  const { reported, total } = runProgress(result);
  if (single) return "Testing this camera… this takes up to a minute.";
  if (total > 0)
    return `Testing cameras… ${reported} of ${total} checked. This usually takes a minute or two.`;
  return "Starting the camera test… this usually takes a minute or two.";
};

export const SiteConnectivityView = ({
  siteId,
  cameraIds,
  canRun,
  buttonLabel,
  connectorRefreshMs,
  prominent = false,
  compact = false,
  cameraAction: cameraActionProp,
  streamTestAvailable,
}: {
  siteId: number;
  /** Narrow everything to these cameras, and test only them. */
  cameraIds?: number[];
  /** The user can edit the site. View Only users see results only. */
  canRun: boolean;
  buttonLabel?: string;
  /** Re-read the connector's report this often (after a first sync). */
  connectorRefreshMs?: number | null;
  prominent?: boolean;
  /** A narrow column: see SiteConnectivityPanel. */
  compact?: boolean;
  /** Per-camera action under each name: see SiteConnectivityPanel. */
  cameraAction?: (camera: CameraConnectivity) => React.ReactNode;
  /**
   * The site About's `stream_test_available`: without a `cameraAction`,
   * each camera gets a "Test stream" button when it's true (ENG-2470).
   */
  streamTestAvailable?: boolean | null;
}) => {
  // Test stream (when the site allows it) sits next to any caller-supplied
  // row action (e.g. Watch live) rather than being replaced by it.
  const testStream =
    streamTestAvailable === true
      ? (camera: CameraConnectivity) => (
          <StreamTestButton
            available
            cameraId={camera.camera_id}
            cameraName={camera.camera_name}
            variant="quiet"
          />
        )
      : undefined;
  const cameraAction =
    testStream || cameraActionProp
      ? (camera: CameraConnectivity) => (
          <span className="inline-flex flex-wrap items-center gap-2">
            {testStream?.(camera)}
            {cameraActionProp?.(camera)}
          </span>
        )
      : undefined;
  const { load, reload } = useSiteConnectivity(siteId, {
    refreshMs: connectorRefreshMs,
  });
  const dryRun = useCameraDryRun(siteId);
  const single = (cameraIds?.length ?? 0) === 1;
  const label = buttonLabel ?? (single ? "Test this camera" : "Test cameras");

  // A test that doesn't cover the cameras in view (a one-camera page after a
  // run on other cameras) isn't this page's answer.
  const testResult =
    dryRun.result &&
    (!cameraIds?.length ||
      (dryRun.result.cameras ?? []).some((c) =>
        cameraIds.includes(c.camera_id)
      ) ||
      dryRun.running)
      ? dryRun.result
      : null;

  const [shown, setShown] = React.useState<Shown>("test");
  // A new test result takes over the view.
  React.useEffect(() => {
    if (dryRun.running) setShown("test");
  }, [dryRun.running]);
  const showing: Shown =
    shown === "test" && (testResult || dryRun.running) ? "test" : "connector";

  const connectorResult = load.status === "ok" ? load.data : null;
  // Email and partner-event sites have no video to open: the check after
  // deploy is their test, so there is no "Run full camera test" for them.
  const family = familyOf(connectorResult ?? dryRun.result);
  const testable = dryRunSupported(family);
  const showRun = canRun && testable;
  // Not on this environment yet (503), or not for this kind of site (fleet).
  const blocked = dryRun.unavailable || dryRun.notSupported;
  const blockedText = dryRun.notSupported
    ? TEST_NOT_SUPPORTED_TEXT
    : TEST_UNAVAILABLE_TEXT;

  return (
    <div className="flex flex-col gap-3" data-testid="connectivity-view">
      <div className="flex flex-wrap items-center gap-3">
        {showRun && !blocked && (
          <TsButton
            onClick={() =>
              dryRun.start(cameraIds?.length ? { camera_ids: cameraIds } : {})
            }
            disabled={dryRun.running}
            data-testid="run-camera-test"
          >
            {dryRun.running ? "Testing…" : label}
          </TsButton>
        )}
        {showRun && blocked && (
          <TsButton disabled data-testid="run-camera-test-unavailable">
            {blockedText}
          </TsButton>
        )}
        {!canRun && testable && (
          <span className="font-mulish text-[13px] text-[#6b7075]">
            You can see results here. Running a camera test needs permission to
            edit this site.
          </span>
        )}
        {(testResult || dryRun.running) && connectorResult && (
          <div
            className="flex rounded border border-[#d1d5db] overflow-hidden font-mulish text-[13px]"
            role="group"
            aria-label="Which result to show"
          >
            {(
              [
                ["test", "Camera test"],
                ["connector", "Connector report"],
              ] as [Shown, string][]
            ).map(([key, text]) => (
              <button
                key={key}
                type="button"
                onClick={() => setShown(key)}
                aria-pressed={showing === key}
                className={`px-3 py-1 ${
                  showing === key
                    ? "bg-actuate-blue text-white font-bold"
                    : "bg-white text-actuate-blue"
                }`}
              >
                {text}
              </button>
            ))}
          </div>
        )}
      </div>

      {!testable && connectorResult && (
        <p
          className="m-0 font-mulish text-[13px] text-[#4c5257]"
          data-testid="camera-test-not-applicable"
        >
          {family === "smtp"
            ? "This site sends us pictures by email, so there is no live video to test: the check below, from the emails we receive, is its test."
            : "This site sends us events from a partner service, so there is no live video to test: the check below, from the events we receive, is its test."}
        </p>
      )}
      {testable && blocked && (
        <p
          className="m-0 font-mulish text-[13px] text-[#4c5257]"
          data-testid="camera-test-unavailable"
        >
          {blockedText}. Below is what the connector itself reports for each
          camera.
        </p>
      )}
      {dryRun.notice && (
        <div
          className="flex items-start justify-between gap-3 rounded border border-[#b25e00] bg-[#fff4e5] px-3 py-2 font-mulish text-[14px] text-actuate-blue"
          role="alert"
          data-testid="camera-test-notice"
        >
          <span>{noticeText(dryRun.notice)}</span>
          <button
            type="button"
            className="text-[13px] font-bold text-[#283e6d] shrink-0"
            onClick={dryRun.clearNotice}
          >
            Dismiss
          </button>
        </div>
      )}

      {showing === "test" ? (
        <TestResult
          result={testResult}
          running={dryRun.running}
          single={single}
          siteId={siteId}
          cameraIds={cameraIds}
          prominent={prominent}
          compact={compact}
          cameraAction={cameraAction}
        />
      ) : load.status === "loading" ? (
        <Spinner text="Loading what the connector reports for each camera…" />
      ) : load.status === "unavailable" ? (
        <p
          className="m-0 py-2 font-mulish text-[14px] text-[#4c5257]"
          data-testid="connector-status-unavailable"
        >
          Camera status isn&apos;t available for this site yet.
          {showRun && !blocked
            ? ` Use “${label}” to check each camera now.`
            : ""}
        </p>
      ) : load.status === "error" ? (
        <div
          className="flex items-center gap-3 py-2 font-mulish text-[14px] text-[#b3261e]"
          role="alert"
        >
          {load.message}
          <TsButton variant="quiet" size="sm" onClick={reload}>
            Try again
          </TsButton>
        </div>
      ) : (
        <SiteConnectivityPanel
          result={load.data}
          siteId={siteId}
          cameraIds={cameraIds}
          prominent={prominent}
          compact={compact}
          cameraAction={cameraAction}
        />
      )}
    </div>
  );
};

const TestResult = ({
  result,
  running,
  single,
  siteId,
  cameraIds,
  prominent,
  compact,
  cameraAction,
}: {
  result: ConnectivityResult | null;
  running: boolean;
  single: boolean;
  siteId: number;
  cameraIds?: number[];
  prominent: boolean;
  compact: boolean;
  cameraAction?: (camera: CameraConnectivity) => React.ReactNode;
}) => {
  if (!result || (running && (result.cameras ?? []).length === 0)) {
    return <Spinner text={runningText(result, single)} />;
  }
  return (
    <div className="flex flex-col gap-3">
      {running && <Spinner text={runningText(result, single)} />}
      {result.status === "failed" && (
        <p
          className="m-0 font-mulish text-[14px] text-[#b3261e]"
          role="alert"
          data-testid="camera-test-failed"
        >
          The camera test couldn&apos;t finish
          {result.error ? `: ${result.error}` : "."} Results below cover the
          cameras it reached.
        </p>
      )}
      {result.status === "timed_out" && (
        <p
          className="m-0 font-mulish text-[14px] text-[#b25e00]"
          role="alert"
          data-testid="camera-test-timed-out"
        >
          The camera test ran out of time. Results below cover the cameras it
          reached; the rest show as not known.
        </p>
      )}
      <SiteConnectivityPanel
        result={result}
        siteId={siteId}
        cameraIds={cameraIds}
        prominent={prominent}
        compact={compact}
        running={running}
        cameraAction={cameraAction}
      />
    </div>
  );
};
